import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import axiosInstance from "../axiosInstance";
import {
  normaliseTaxonomies,
  normaliseTaxonomy,
  taxonomyLabel,
} from "../utils/shopifyTaxonomy";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const getSavedTaxonomies = (tenantDetails) => {
  const detailedTaxonomies = normaliseTaxonomies(
    tenantDetails?.shopifyTaxonomies ?? tenantDetails?.ShopifyTaxonomies ?? []
  );
  const taxonomyIds = tenantDetails?.shopifyTaxonomyIds ?? tenantDetails?.ShopifyTaxonomyIds;
  if (!Array.isArray(taxonomyIds)) return detailedTaxonomies;

  return taxonomyIds.map((taxonomyId) => (
    detailedTaxonomies.find((taxonomy) => taxonomy.id === taxonomyId)
    || normaliseTaxonomy(taxonomyId)
  ));
};

export default function TenantTaxonomySection({ tenantDetails, setTenantDetails }) {
  const tenantId = tenantDetails?.id || "";
  const savedTaxonomies = useMemo(() => getSavedTaxonomies(tenantDetails), [tenantDetails]);
  const savedIdsKey = savedTaxonomies.map((taxonomy) => taxonomy.id).join("\u0000");
  const [selectedTaxonomies, setSelectedTaxonomies] = useState(savedTaxonomies);
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [pageCursors, setPageCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryVersion, setRetryVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const requestIdRef = useRef(0);

  const selectedIds = useMemo(
    () => new Set(selectedTaxonomies.map((taxonomy) => taxonomy.id)),
    [selectedTaxonomies]
  );

  useEffect(() => {
    setSelectedTaxonomies((current) => (
      current.map((taxonomy) => taxonomy.id).join("\u0000") === savedIdsKey ? current : savedTaxonomies
    ));
    setFeedback(null);
    // Reset only when the selected tenant or its saved taxonomy IDs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, savedIdsKey]);

  const fetchTaxonomies = useCallback(async () => {
    if (!tenantId) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const params = { pageSize: PAGE_SIZE };
    const normalizedSearch = searchText.trim();
    const cursor = pageCursors[pageIndex];
    if (normalizedSearch) params.search = normalizedSearch;
    if (cursor) params.cursor = cursor;

    setLoading(true);
    setLoadError("");
    try {
      const { data } = await axiosInstance.get("/shopify-taxonomy", { params });
      if (requestId !== requestIdRef.current) return;
      const incomingItems = normaliseTaxonomies(data?.items);
      setItems(incomingItems);
      setNextCursor(data?.nextCursor || null);
      setSelectedTaxonomies((current) => current.map((selected) => {
        const loaded = incomingItems.find((item) => item.id === selected.id);
        return loaded && taxonomyLabel(selected) === selected.id ? loaded : selected;
      }));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setItems([]);
      setNextCursor(null);
      setLoadError("Could not load Shopify taxonomy categories.");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [pageCursors, pageIndex, searchText, tenantId]);

  useEffect(() => {
    const timeoutId = setTimeout(fetchTaxonomies, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [fetchTaxonomies, retryVersion]);

  useEffect(() => () => { requestIdRef.current += 1; }, []);

  const toggleTaxonomy = (taxonomy) => {
    setSelectedTaxonomies((current) => (
      current.some((selected) => selected.id === taxonomy.id)
        ? current.filter((selected) => selected.id !== taxonomy.id)
        : [...current, taxonomy]
    ));
    setFeedback(null);
  };

  const goToNextPage = () => {
    if (!nextCursor || loading) return;
    setPageCursors((current) => [...current.slice(0, pageIndex + 1), nextCursor]);
    setPageIndex((current) => current + 1);
  };

  const saveTaxonomies = async () => {
    if (!tenantId || saving) return;
    const shopifyTaxonomyIds = selectedTaxonomies.map((taxonomy) => taxonomy.id);
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put(`/tenant/${encodeURIComponent(tenantId)}/shopify-taxonomy`, { shopifyTaxonomyIds });
      setTenantDetails?.((previous) => ({ ...previous, shopifyTaxonomyIds }));
      setFeedback({ severity: "success", message: "Shopify taxonomy saved successfully." });
    } catch (error) {
      setFeedback({ severity: "error", message: error.response?.data?.message || "Unable to save Shopify taxonomy." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid rgba(15, 23, 42, 0.1)", overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
          <Typography sx={{ fontWeight: 800, color: "#202531", mb: 0.5 }}>Shopify product taxonomy</Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
            Search and select any number of categories. Results are loaded one page at a time from Shopify&apos;s taxonomy catalog.
          </Typography>
          <TextField
            fullWidth size="small" label="Search taxonomy" placeholder="Search by category name or path"
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setPageCursors([null]);
              setPageIndex(0);
            }}
            disabled={!tenantId}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </Box>

        <Box sx={{ minHeight: 220, maxHeight: 520, overflowY: "auto" }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 220 }}>
              <CircularProgress size={24} sx={{ color: "#f97316" }} />
              <Typography variant="body2" sx={{ color: "#6b7280" }}>Loading taxonomy categories…</Typography>
            </Stack>
          ) : loadError ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 220, p: 2 }}>
              <Typography role="alert" variant="body2" sx={{ color: "#b91c1c" }}>{loadError}</Typography>
              <Button size="small" onClick={() => setRetryVersion((version) => version + 1)} sx={{ color: "#ea580c", textTransform: "none", fontWeight: 700 }}>Retry</Button>
            </Stack>
          ) : items.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 8 }}>No taxonomy categories match this search.</Typography>
          ) : items.map((taxonomy) => (
            <Box key={taxonomy.id} sx={{ px: 2, borderBottom: "1px solid rgba(15, 23, 42, 0.06)", backgroundColor: selectedIds.has(taxonomy.id) ? "rgba(255, 241, 233, 0.62)" : "#fff", "&:last-child": { borderBottom: 0 } }}>
              <FormControlLabel
                sx={{ m: 0, width: "100%", py: 0.6, alignItems: "flex-start" }}
                control={<Checkbox checked={selectedIds.has(taxonomy.id)} onChange={() => toggleTaxonomy(taxonomy)} color="warning" />}
                label={(
                  <Box sx={{ minWidth: 0, pt: 0.7 }}>
                    <Typography sx={{ color: "#374151", fontSize: "0.9rem", fontWeight: 700 }}>{taxonomy.name || taxonomy.id}</Typography>
                    {taxonomy.fullPath && taxonomy.fullPath !== taxonomy.name && (
                      <Typography variant="caption" sx={{ display: "block", color: "#6b7280", overflowWrap: "anywhere" }}>{taxonomy.fullPath}</Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          ))}
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(15, 23, 42, 0.08)", backgroundColor: "#fffaf7" }}>
          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 700 }}>Page {pageIndex + 1} · {selectedTaxonomies.length.toLocaleString()} selected</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" onClick={() => setPageIndex((current) => current - 1)} disabled={pageIndex === 0 || loading} sx={{ borderRadius: "10px", textTransform: "none" }}>Previous</Button>
            <Button variant="outlined" size="small" onClick={goToNextPage} disabled={!nextCursor || loading} sx={{ borderRadius: "10px", textTransform: "none", borderColor: "#f97316", color: "#ea580c" }}>Next</Button>
          </Stack>
        </Stack>
      </Paper>

      {selectedTaxonomies.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: "1px solid rgba(15, 23, 42, 0.1)" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
            <Typography sx={{ fontWeight: 800, color: "#202531" }}>Selected ({selectedTaxonomies.length.toLocaleString()})</Typography>
            <Button type="button" size="small" onClick={() => { setSelectedTaxonomies([]); setFeedback(null); }} disabled={saving} sx={{ color: "#6b7280", textTransform: "none", fontWeight: 700 }}>Clear all</Button>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ maxHeight: 220, overflowY: "auto" }}>
            {selectedTaxonomies.map((taxonomy) => (
              <Chip key={taxonomy.id} label={taxonomy.id} onDelete={() => toggleTaxonomy(taxonomy)} sx={{ maxWidth: "100%", backgroundColor: "#fff1e9", color: "#9a3412" }} />
            ))}
          </Stack>
        </Paper>
      )}

      {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={saveTaxonomies} disabled={saving || !tenantId} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, backgroundColor: "#f97316", "&:hover": { backgroundColor: "#ea580c" } }}>
          {saving ? "Saving…" : "Save Taxonomy"}
        </Button>
      </Box>
    </Stack>
  );
}
